import React, { useCallback, useEffect, useState } from "react";
import { ShieldCheck, Inbox, Check, X } from "lucide-react";
import api, { friendlyError } from "@/lib/api";
import { Card, Button, Chip, Skeleton, EmptyState, Toast } from "@/ui";
import ConfirmAction from "@/components/ConfirmAction";
import { manilaDateTime } from "@/lib/format";

// The approval inbox (Rule 3). Back-office MCP tools that move money, change a
// price or message a person do not act — they land here for a person to say yes.
export default function McpApprovals({ shopId }) {
  const [pending, setPending] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // { action, mode: 'approve'|'reject' }

  const load = useCallback(() => {
    if (!shopId) return;
    setLoading(true);
    api.get(`/mcp/pending?shopId=${shopId}`)
      .then((r) => setPending(r.data.pending || []))
      .catch((e) => setError(friendlyError(e)))
      .finally(() => setLoading(false));
  }, [shopId]);

  useEffect(load, [load]);

  const run = async () => {
    if (!confirm) return;
    setBusy(true); setError("");
    const { action, mode } = confirm;
    try {
      await api.post(`/mcp/pending/${action.id}/${mode}`, { shopId });
      setToast(mode === "approve" ? "Done — the action was carried out" : "That action was turned down");
      load();
    } catch (e) { setError(friendlyError(e)); }
    finally { setBusy(false); setConfirm(null); setTimeout(() => setToast(""), 3000); }
  };

  return (
    <Card data-testid="mcp-approvals-card">
      <div className="row-between" style={{ marginBottom: 8 }}>
        <div className="row" style={{ gap: 10 }}>
          <span className="ph-icon" style={{ margin: 0 }}><Inbox size={18} strokeWidth={1.75} /></span>
          <h2 className="t-h2">needs your approval</h2>
        </div>
        {pending && pending.length ? <Chip tone="warn" dot data-testid="mcp-pending-count">{pending.length} waiting</Chip> : null}
      </div>
      <p className="muted t-small" style={{ marginBottom: 14, maxWidth: 620 }}>
        When an assistant tries to move money, change a price or message someone, it stops here first.
        Nothing happens until you say yes.
      </p>

      {error ? <div className="err-text" data-testid="mcp-approvals-error" style={{ marginBottom: 12 }}>{error}</div> : null}

      {loading || pending === null ? (
        <Skeleton variant="card" />
      ) : pending.length === 0 ? (
        <EmptyState icon={<ShieldCheck size={20} strokeWidth={1.75} />} title="nothing waiting — you are all caught up" />
      ) : (
        <div className="stack" style={{ gap: 12 }}>
          {pending.map((p) => (
            <div key={p.id} className="tbl-wrap" style={{ padding: 12 }} data-testid={`mcp-pending-${p.id}`}>
              <div className="row-between" style={{ marginBottom: 8 }}>
                <Chip tone="neutral">{p.tool}</Chip>
                <span className="muted-3 t-small">{p.createdAt ? manilaDateTime(p.createdAt) : ""}</span>
              </div>
              <ul className="stack" style={{ listStyle: "none", padding: 0, margin: "0 0 10px" }}>
                {(p.preview || []).map((line, i) => <li key={i} className="t-body">{line}</li>)}
              </ul>
              <div className="row" style={{ gap: 8 }}>
                <Button variant="primary" size="sm" data-testid={`mcp-approve-${p.id}`}
                  onClick={() => setConfirm({ action: p, mode: "approve" })}>
                  <Check size={14} strokeWidth={2} />approve
                </Button>
                <Button variant="danger" size="sm" data-testid={`mcp-reject-${p.id}`}
                  onClick={() => setConfirm({ action: p, mode: "reject" })}>
                  <X size={14} strokeWidth={2} />turn down
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmAction
        open={!!confirm}
        title={confirm?.mode === "approve" ? "Approve this action?" : "Turn down this action?"}
        preview={confirm ? [...(confirm.action.preview || []),
          confirm.mode === "approve" ? "It will be carried out right away." : "Nothing will happen."] : []}
        confirmLabel={confirm?.mode === "approve" ? "Yes, do it" : "Yes, turn it down"}
        busy={busy} error={error} onConfirm={run} onCancel={() => setConfirm(null)}
      />

      {toast ? <div className="toast-wrap"><Toast tone="good" data-testid="mcp-approvals-toast">{toast}</Toast></div> : null}
    </Card>
  );
}
